import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EMPTY, Subject, catchError, finalize, first, forkJoin, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { RequestModel } from 'src/app/core/models/request-model.interface';
import { CustomFile } from 'src/app/core/models/customFile.interface';
import { AssignmentReportCreate } from 'src/app/core/models/project/assignment/report/assignment-report-create.interface';
import { AssignmentReportFormModel } from 'src/app/core/models/project/assignment/report/assignment-report-form.model';
import { AssignmentReport } from 'src/app/core/models/project/assignment/report/assignment-report.interface';
import { ReportType } from 'src/app/core/models/report-type/report-type.interface';
import { AssignmentReportService } from 'src/app/core/services/assignment-report.service';
import { FileService } from 'src/app/core/services/file.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Util } from 'src/app/core/utils/util';

interface AssignmentReportVm extends AssignmentReport {
  sanitizedComment?: SafeHtml;
}

@Component({
  selector: 'app-assignment-reports',
  templateUrl: './assignment-reports.component.html',
  styleUrls: ['./assignment-reports.component.scss'],
  providers: [AssignmentReportFormModel],
  standalone: false,
})
export class AssignmentReportsComponent implements OnChanges, OnDestroy {
  formModel = inject(AssignmentReportFormModel);
  private readonly assignmentReportService = inject(AssignmentReportService);
  private readonly fileService = inject(FileService);
  private readonly toastService = inject(ToastService);
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public projectId = '';
  @Input({ required: true }) public assignmentId = '';
  @Input() public isReadonly = false;

  @ViewChild('successCreateTpl')
  public successCreateTpl!: TemplateRef<unknown>;

  @ViewChild('successDeleteTpl')
  public successDeleteTpl!: TemplateRef<unknown>;

  @ViewChild('successUpdateTpl')
  public successUpdateTpl!: TemplateRef<unknown>;

  @ViewChild('dangerTpl')
  public dangerTpl!: TemplateRef<unknown>;

  @ViewChild('reportForm')
  public reportFormElement?: ElementRef<HTMLFormElement>;

  public readonly uploadFieldId = 'assignment-report-upload';

  public reportTypes: ReportType[] = [];
  public reportsPage: PagedList<AssignmentReportVm> = {
    items: [],
    page: 1,
    pageSize: 10,
    totalCount: 0,
  };
  public isLoading = false;
  public isSaving = false;
  public deletingReportId: string | null = null;
  public editingReportId: string | null = null;
  public uploadPath = '';

  public selectedReportTypeUnits = '';
  public pendingAttachment: {
    path: string;
    name: string;
    size: number;
    uploadId: string | null;
    bucket: string | null;
    eTag: string | null;
    versionId: string | null;
    contentType: string | null;
    correlationId: string | null;
  } | null = null;

  public editAttachment: {
    path: string;
    name: string;
    size: number;
    uploadId: string | null;
    bucket: string | null;
    eTag: string | null;
    versionId: string | null;
    contentType: string | null;
    correlationId: string | null;
  } | null = null;

  public readonly request: RequestModel = {
    filters: [],
    page: 1,
    pageSize: 10,
    sortColumn: 'reportDate',
    sortOrder: 'desc',
  };

  private readonly unsubscribe$ = new Subject<void>();
  private reportTypeValueSubscriptionInitialized = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['assignmentId'] || !this.assignmentId || !this.companyId || !this.projectId) {
      return;
    }

    this.uploadPath = `companies/${this.companyId}/projects/${this.projectId}/assignments/${this.assignmentId}/assignment-reports`;
    this.request.page = 1;

    if (!this.reportTypeValueSubscriptionInitialized) {
      this.formModel.form.controls.reportTypeId.valueChanges
        .pipe(
          takeUntil(this.unsubscribe$),
          tap((reportTypeId) => {
            this.selectedReportTypeUnits = this.reportTypes.find((x) => x.id === reportTypeId)?.units ?? '';
          }),
        )
        .subscribe();

      this.reportTypeValueSubscriptionInitialized = true;
    }

    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onUploadFile(event: CustomFile): void {
    const storageKey = event.upload?.key?.trim();

    if (!storageKey) {
      this.pendingAttachment = null;
      return;
    }

    this.pendingAttachment = {
      path: storageKey,
      name: event.file.name,
      size: event.file.size,
      uploadId: event.upload?.uploadId ?? null,
      bucket: event.upload?.bucket ?? null,
      eTag: event.upload?.eTag ?? null,
      versionId: event.upload?.versionId ?? null,
      contentType: event.upload?.contentType ?? null,
      correlationId: event.upload?.correlationId ?? null,
    };
  }

  public onPageChange(page: number): void {
    if (this.request.page === page) {
      return;
    }

    this.request.page = page;
    this.loadReportsPage();
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;
    this.request.page = 1;
    this.loadReportsPage();
  }

  public removePendingAttachment(): void {
    if (!this.pendingAttachment?.path) {
      this.pendingAttachment = null;
      return;
    }

    this.fileService
      .delete(this.pendingAttachment.path)
      .pipe(
        first(),
        tap(() => {
          this.pendingAttachment = null;
        }),
        catchError((error) => {
          console.log(error);
          this.showErrorToast();
          return EMPTY;
        }),
      )
      .subscribe();
  }

  public editReport(report: AssignmentReportVm): void {
    if (this.isReadonly || this.isSaving) {
      return;
    }

    this.editingReportId = report.id;
    this.formModel.form.patchValue({
      reportTypeId: report.reportTypeId,
      value: report.value,
      reportDate: report.reportDate?.split('T')[0] ?? '',
      comment: report.comment ?? null,
    });
    this.selectedReportTypeUnits = report.units;
    this.pendingAttachment = null;
    this.editAttachment = report.attachmentPath && report.attachmentName
      ? {
        path: report.attachmentPath,
        name: report.attachmentName,
        size: report.attachmentSize ?? 0,
        uploadId: report.uploadId,
        bucket: report.attachmentBucket,
        eTag: report.attachmentETag,
        versionId: report.attachmentVersionId,
        contentType: report.attachmentContentType,
        correlationId: null,
      }
      : null;
    this.scrollFormIntoView();
  }

  public cancelEdit(): void {
    this.resetFormState();
  }

  public removeEditAttachment(): void {
    this.editAttachment = null;
  }

  public saveReport(): void {
    if (!this.assignmentId || this.isSaving || this.isReadonly) {
      return;
    }

    if (this.formModel.form.invalid) {
      this.formModel.form.markAllAsTouched();
      return;
    }

    const isUpdate = !!this.editingReportId;
    const editedReportId = this.editingReportId;
    const value = this.formModel.form.getRawValue();
    const previousAttachmentPath = editedReportId
      ? this.reportsPage.items.find((report) => report.id === editedReportId)?.attachmentPath ?? null
      : null;
    const payload: AssignmentReportCreate = {
      reportTypeId: value.reportTypeId,
      value: value.value ?? 0,
      reportDate: value.reportDate,
      comment: value.comment?.trim() ? value.comment.trim() : null,
      attachmentPath: this.pendingAttachment?.path ?? this.editAttachment?.path ?? null,
      attachmentName: this.pendingAttachment?.name ?? this.editAttachment?.name ?? null,
      attachmentSize: this.pendingAttachment?.size ?? this.editAttachment?.size ?? null,
      uploadId: this.pendingAttachment?.uploadId ?? this.editAttachment?.uploadId ?? null,
      attachmentBucket: this.pendingAttachment?.bucket ?? this.editAttachment?.bucket ?? null,
      attachmentKey: this.pendingAttachment?.path ?? this.editAttachment?.path ?? null,
      attachmentETag: this.pendingAttachment?.eTag ?? this.editAttachment?.eTag ?? null,
      attachmentVersionId: this.pendingAttachment?.versionId ?? this.editAttachment?.versionId ?? null,
      attachmentContentType: this.pendingAttachment?.contentType ?? this.editAttachment?.contentType ?? null,
    };

    this.isSaving = true;

    const request$ = this.editingReportId
      ? this.assignmentReportService.updateAssignmentReport(
          this.assignmentId,
          this.editingReportId,
          payload,
          this.pendingAttachment?.correlationId ?? undefined,
        )
      : this.assignmentReportService.createAssignmentReport(
          this.assignmentId,
          payload,
          this.pendingAttachment?.correlationId ?? undefined,
        );

    request$
      .pipe(
        first(),
        switchMap((savedReport) => {
          if (!this.pendingAttachment?.path) {
            return of(savedReport);
          }

          return this.fileService.deleteTemporaryFileInfo(this.pendingAttachment.path).pipe(
            first(),
            map(() => savedReport),
            catchError(() => of(savedReport)),
          );
        }),
        switchMap(() => {
          const currentAttachmentPath = payload.attachmentPath;
          const shouldDeletePreviousAttachment =
            !!previousAttachmentPath && previousAttachmentPath !== currentAttachmentPath;

          if (!shouldDeletePreviousAttachment) {
            return of(void 0);
          }

          return this.fileService.delete(previousAttachmentPath).pipe(
            first(),
            catchError(() => EMPTY),
            map(() => void 0),
          );
        }),
        tap(() => {
          if (!isUpdate) {
            this.request.page = 1;
          }
        }),
        switchMap(() => this.fetchReportsPage()),
        tap((reportsPage) => {
          this.applyReportsPage(reportsPage);
          this.resetFormState();
          this.toastService.show({
            template: isUpdate ? this.successUpdateTpl : this.successCreateTpl,
            className: 'bg-success text-light',
          });
        }),
        catchError((error) => {
          console.log(error);
          this.showErrorToast();
          return EMPTY;
        }),
        finalize(() => {
          this.isSaving = false;
        }),
      )
      .subscribe();
  }

  public deleteReport(report: AssignmentReportVm): void {
    if (this.isReadonly || this.isSaving || this.deletingReportId) {
      return;
    }

    this.deletingReportId = report.id;

    this.assignmentReportService
      .deleteAssignmentReport(this.assignmentId, report.id)
      .pipe(
        first(),
        switchMap(() => {
          if (!report.attachmentPath) {
            return of(void 0);
          }

          return this.fileService.delete(report.attachmentPath).pipe(
            first(),
            catchError(() => EMPTY),
            map(() => void 0),
          );
        }),
        tap(() => {
          const nextTotalCount = Math.max(0, this.reportsPage.totalCount - 1);
          const maxPage = Math.max(1, Math.ceil(nextTotalCount / this.request.pageSize));
          this.request.page = Math.min(this.request.page, maxPage);
        }),
        switchMap(() => this.fetchReportsPage()),
        tap((reportsPage) => {
          this.applyReportsPage(reportsPage);
          this.toastService.show({
            template: this.successDeleteTpl,
            className: 'bg-success text-light',
          });
        }),
        catchError((error) => {
          console.log(error);
          this.showErrorToast();
          return EMPTY;
        }),
        finalize(() => {
          this.deletingReportId = null;
        }),
      )
      .subscribe();
  }

  public downloadAttachment(report: AssignmentReportVm): void {
    if (!report.attachmentPath || !report.attachmentName) {
      return;
    }

    this.fileService
      .downloadFile(report.attachmentKey ?? report.attachmentPath, report.attachmentName, {
        uploadId: report.uploadId,
        fileId: report.id,
      })
      .pipe(
        first(),
        tap((blob) => {
          Util.saveAs(blob, report.attachmentName!);
        }),
        catchError((error) => {
          console.log(error);
          this.showErrorToast();
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private loadInitialData(): void {
    this.isLoading = true;

    forkJoin([
      this.assignmentReportService.getAssignmentReportTypes(this.assignmentId),
      this.fetchReportsPage(),
    ])
      .pipe(
        first(),
        tap(([reportTypes, reportsPage]) => {
          this.reportTypes = reportTypes;
          this.applyReportsPage(reportsPage);
          this.formModel.form.patchValue({
            reportDate: Util.getFormattedStringDate(new Date()),
          });
        }),
        catchError((error) => {
          console.log(error);
          this.reportTypes = [];
          this.applyReportsPage({
            items: [],
            page: this.request.page,
            pageSize: this.request.pageSize,
            totalCount: 0,
          });
          this.showErrorToast();
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private loadReportsPage(): void {
    this.isLoading = true;

    this.fetchReportsPage()
      .pipe(
        first(),
        tap((reportsPage) => {
          this.applyReportsPage(reportsPage);
        }),
        catchError((error) => {
          console.log(error);
          this.showErrorToast();
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private fetchReportsPage() {
    return this.assignmentReportService.getAssignmentReportsPaged(this.assignmentId, this.request);
  }

  private applyReportsPage(reportsPage: PagedList<AssignmentReport>): void {
    this.reportsPage = {
      ...reportsPage,
      items: reportsPage.items.map((report) => this.toVm(report)),
    };
  }

  private resetFormState(): void {
    this.formModel.form.reset({
      reportTypeId: '',
      value: null,
      reportDate: Util.getFormattedStringDate(new Date()),
      comment: null,
    });
    this.selectedReportTypeUnits = '';
    this.pendingAttachment = null;
    this.editAttachment = null;
    this.editingReportId = null;
  }

  private scrollFormIntoView(): void {
    setTimeout(() => {
      this.reportFormElement?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  private showErrorToast(): void {
    this.toastService.show({
      template: this.dangerTpl,
      className: 'bg-danger text-light',
    });
  }

  private toVm(report: AssignmentReport): AssignmentReportVm {
    return {
      ...report,
      sanitizedComment: report.comment
        ? this.sanitizer.bypassSecurityTrustHtml(report.comment)
        : undefined,
    };
  }
}
